// Vocabulary + checks for the Module 6 teacher workflow surface.
// `display_status` comes from the server; the stage is the fallback.

export const STATUS_META = {
  teacher_draft: {
    label: "Draft",
    pill: "bg-slate-100 text-slate-700 border-slate-200",
    hint: "Fill in your plan, then submit it to your HOD for review.",
  },
  changes_requested: {
    label: "Changes requested",
    pill: "bg-rose-50 text-rose-800 border-rose-200",
    hint: "Your reviewer asked for changes. Update the plan and submit it again.",
  },
  hod_review: {
    label: "With HOD",
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    hint: "Your HOD is reviewing this plan. You can recall it to keep editing.",
  },
  admin_review: {
    label: "With Admin",
    pill: "bg-blue-50 text-blue-800 border-blue-200",
    hint: "Your HOD approved it. It is now waiting for final admin approval.",
  },
  approved: {
    label: "Approved",
    pill: "bg-emerald-50 text-emerald-800 border-emerald-200",
    hint: "This plan is approved and locked.",
  },
};

export function displayStatusOf(plan) {
  if (!plan) return "teacher_draft";
  const stage = plan.workflow_stage || plan.workflowStage || "teacher_draft";
  const display = plan.display_status;
  return STATUS_META[display] ? display : stage;
}

export function statusMeta(plan) {
  return STATUS_META[displayStatusOf(plan)] || STATUS_META.teacher_draft;
}

// Mirrors Backend validate_for_submit(); the server stays the authority.
export function readinessProblems(plan) {
  const problems = [];
  const chapters = [];
  (plan?.terms || []).forEach((term) => {
    (term.units || []).forEach((unit) => {
      (unit.chapters || []).forEach((ch) => {
        chapters.push({
          term: term.termNo,
          unit: unit.unitName || unit.unitNo || "Unit",
          chapter: ch,
        });
      });
    });
  });
  if (!chapters.length) {
    problems.push("Add at least one chapter before submitting.");
    return problems;
  }
  chapters.forEach(({ term, unit, chapter }) => {
    const label = `Term ${term} › ${unit} › ${chapter.chapterName || chapter.chapterNo || "Chapter"}`;
    if (!String(chapter.chapterName || "").trim()) problems.push(`${label}: chapter name is missing.`);
    if (!(chapter.learningObjectives || []).some((x) => String(x || "").trim())) {
      problems.push(`${label}: add at least one learning objective.`);
    }
    const comps = [...(chapter.competencies || []), ...(chapter.customCompetencies || [])];
    if (!comps.some((x) => String(x || "").trim())) problems.push(`${label}: select at least one competency.`);
  });
  return problems;
}

export function describeHistoryEntry(entry) {
  switch (entry.action) {
    case "submit":
      return "You submitted this plan to your HOD";
    case "recall":
      return "You recalled this plan";
    case "approve":
      return entry.role === "admin" ? "Approved by admin" : "Approved by HOD, sent to admin";
    case "request_changes":
      return entry.from === "admin_review" && entry.role === "admin" ? "Admin sent it back to your HOD" : "Changes requested";
    case "request_changes_teacher":
      return "Admin sent it back to you";
    case "material_edit":
      return "Edited after approval; re-approval required";
    case "content_edit":
      return "Content edited by a reviewer";
    default:
      return entry.action || "Updated";
  }
}

export function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
